import { useRef, useState } from 'react';
import { IconFileTypePdf, IconUpload } from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../../i18n/hooks/useTranslation';
import { importAtsCvFromPdf } from '../engines/importAtsCvFromPdf.js';

const MAX_PDF_MB = 8;

const isPdf = (file) => file.type === 'application/pdf' || /\.pdf$/i.test(file.name || '');

export default function AtsCvPdfImport({ onImported }) {
    const { t } = useTranslation();
    const [importing, setImporting] = useState(false);
    const [progress, setProgress] = useState(0);
    const [dragging, setDragging] = useState(false);
    const inputRef = useRef(null);
    // Drag events fire for child elements too, so nesting is counted rather than
    // clearing the highlight on the first dragleave.
    const dragDepth = useRef(0);

    const reject = (messageKey) => {
        notifications.show({
            title: t('atsCvMaker.import.failTitle'),
            message: t(messageKey, { maxMb: MAX_PDF_MB }),
            color: 'red',
        });
    };

    const handleFile = async (file) => {
        if (!file) return;
        if (!isPdf(file)) {
            reject('atsCvMaker.import.notPdf');
            return;
        }
        if (file.size > MAX_PDF_MB * 1024 * 1024) {
            reject('atsCvMaker.import.tooLarge');
            return;
        }

        setImporting(true);
        setProgress(0);
        try {
            const result = await importAtsCvFromPdf(file, setProgress);
            onImported(result.form, {
                warnings: result.warnings,
                lineCount: result.lineCount,
                pageCount: result.pageCount,
            });

            const warnMsg = result.warnings.length ? ` ${result.warnings[0]}` : '';
            notifications.show({
                title: t('atsCvMaker.import.successTitle'),
                message: t('atsCvMaker.import.successMessage', {
                    pages: result.pageCount,
                    warning: warnMsg,
                }),
                color: result.warnings.length ? 'yellow' : 'green',
            });
        } catch (err) {
            notifications.show({
                title: t('atsCvMaker.import.failTitle'),
                message: err?.message || t('atsCvMaker.import.failMessage'),
                color: 'red',
            });
        } finally {
            setImporting(false);
            setProgress(0);
            if (inputRef.current) inputRef.current.value = '';
        }
    };

    const handleDragEnter = (e) => {
        if (importing) return;
        e.preventDefault();
        dragDepth.current += 1;
        setDragging(true);
    };

    const handleDragOver = (e) => {
        if (importing) return;
        // Without this the browser navigates to the dropped file instead.
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        dragDepth.current = Math.max(0, dragDepth.current - 1);
        if (dragDepth.current === 0) setDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        dragDepth.current = 0;
        setDragging(false);
        if (importing) return;
        handleFile(e.dataTransfer.files?.[0]);
    };

    return (
        <div className="ats-cv-pdf-import">
            <input
                ref={inputRef}
                type="file"
                accept="application/pdf,.pdf"
                className="hidden"
                disabled={importing}
                onChange={(e) => handleFile(e.target.files?.[0])}
            />
            <button
                type="button"
                disabled={importing}
                onClick={() => inputRef.current?.click()}
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`flex w-full min-h-[100px] flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed px-4 py-5 text-center transition-colors disabled:opacity-60 ${
                    dragging
                        ? 'border-violet-500 bg-violet-100 dark:border-violet-500 dark:bg-violet-950/50'
                        : 'border-violet-300 bg-violet-50 hover:border-violet-400 hover:bg-violet-100 dark:border-violet-800 dark:bg-violet-950/20 dark:hover:bg-violet-950/40'
                }`}
            >
                {importing ? (
                    <IconFileTypePdf size={28} className="text-violet-600 dark:text-violet-400" />
                ) : (
                    <IconUpload size={26} className="text-violet-600 dark:text-violet-400" />
                )}
                <div>
                    <p className="text-[13.5px] font-semibold text-[#1C1815] dark:text-zinc-100">
                        {dragging ? t('atsCvMaker.import.dropActive') : t('atsCvMaker.import.dropTitle')}
                    </p>
                    <p className="mt-1 text-[12px] text-[#6B635C] dark:text-zinc-400">
                        {t('atsCvMaker.import.dropHint', { maxMb: MAX_PDF_MB })}
                    </p>
                </div>
            </button>

            {importing ? (
                <div className="mt-3">
                    <p className="mb-1 text-[11px] text-[#6B635C] dark:text-zinc-400">
                        {progress < 45
                            ? t('atsCvMaker.import.reading')
                            : progress < 100
                                ? t('atsCvMaker.import.ocr')
                                : t('atsCvMaker.import.finishing')}{' '}
                        {progress}%
                    </p>
                    <div className="h-2 overflow-hidden rounded-full bg-violet-100 dark:bg-violet-950/40">
                        <div
                            className="h-full rounded-full bg-violet-500 transition-all duration-150"
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                </div>
            ) : null}
        </div>
    );
}
